import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { hasCapability, homePathForUser, isCompanyStaff, isSubscribed, ROLES } from '../utils/roles.js';

/**
 * @param {{
 *   children: import('react').ReactNode,
 *   roles?: string[],
 *   capabilities?: string[],
 *   requireSubscription?: boolean,
 *   allowWithoutSubscription?: boolean,
 * }} props
 */
export function ProtectedRoute({
  children,
  roles,
  capabilities,
  requireSubscription = false,
  allowWithoutSubscription = false,
}) {
  const { user, booting } = useAuth();
  const location = useLocation();

  if (booting) {
    return (
      <div className="auth-shell">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (roles?.length && !roles.includes(user.role || ROLES.CLIENT)) {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  if (capabilities?.length && !capabilities.every((cap) => hasCapability(user, cap))) {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  if (user.role === ROLES.CLIENT && !hasCapability(user, 'view_portal')) {
    return (
      <div className="auth-shell">
        <p className="err">Client portal access is not included in your agency&apos;s plan.</p>
      </div>
    );
  }

  if (requireSubscription && isCompanyStaff(user) && !isSubscribed(user) && !allowWithoutSubscription) {
    return <Navigate to="/admin/billing" replace state={{ from: location.pathname }} />;
  }

  if (
    !allowWithoutSubscription &&
    isCompanyStaff(user) &&
    !isSubscribed(user) &&
    location.pathname.startsWith('/admin') &&
    location.pathname !== '/admin/billing'
  ) {
    return <Navigate to="/admin/billing" replace />;
  }

  return children;
}
